// src/pages/chat/ChatCreatePage.tsx
// "+ New conversation" — desktop-app create dialog (docs/ux/14-forms.md)
// built on the form-kit (FormDialog/TitleInput/AutoGrowTextarea/
// SegmentedControl) from src/components/form. Member search reuses the
// shared UserPicker instead of a hand-rolled debounced dropdown.

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import { UserPicker } from '@/components/UserPicker';
import { FormDialog, TitleInput, AutoGrowTextarea, SegmentedControl } from '@/components/form';
import type { User } from '@core/types/user';
import {
  type NewConversationType,
  type CreateConversationData,
  validateCreateConversation,
  createConversation,
  memberFromPick,
  getUserDisplayName,
  getInitials,
} from '@/features/chat/logic';

const TYPE_ICON_PATHS: Record<NewConversationType, string> = {
  direct: 'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z',
  group: 'M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z',
  channel: 'M5.25 8.25h15m-16.5 7.5h15m-1.8-13.5-3.9 19.5m-2.1-19.5-3.9 19.5',
};

function TypeIcon({ type }: { type: NewConversationType }) {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d={TYPE_ICON_PATHS[type]} />
    </svg>
  );
}

export const ChatCreatePage = observer(() => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const [selectedType, setSelectedType] = useState<NewConversationType>('direct');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<User[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);

  const close = useCallback(() => navigate('/chats'), [navigate]);

  const dirty = !!(name.trim() || description.trim() || selectedMembers.length > 0);

  const handleSelectType = (value: string) => {
    setSelectedType(value as NewConversationType);
    setErrors({});
    setSubmitError(null);
  };

  const handleRemoveMember = useCallback((userId: string) => {
    setSelectedMembers(prev => prev.filter(m => m.entity?.id !== userId));
  }, []);

  const handlePick = useCallback(async (userId: string, displayName: string) => {
    if (!userId) return;
    if (selectedMembers.some(m => m.entity?.id === userId)) return;
    const picked = memberFromPick(userId, displayName);

    if (selectedType === 'direct') {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const id = await createConversation({
          type: 'direct',
          name: '',
          description: '',
          members: [picked],
        });
        navigate(`/chats/${id}`);
      } catch (err) {
        setSubmitError(err);
        setIsSubmitting(false);
      }
      return;
    }

    setSelectedMembers(prev => [...prev, picked]);
    setErrors(prev => ({ ...prev, members: '' }));
  }, [selectedMembers, selectedType, navigate]);

  const handleSubmit = async () => {
    if (selectedType === 'direct') return;

    const data: CreateConversationData = {
      type: selectedType,
      name,
      description,
      members: selectedMembers,
    };

    const validationErrors = validateCreateConversation(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setErrors({});

    try {
      const id = await createConversation(data);
      navigate(`/chats/${id}`);
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = selectedType !== 'direct' && !!name.trim();

  return (
    <FormDialog
      open
      onClose={close}
      onSubmit={handleSubmit}
      submitLabel={t('chat.create.createButton')}
      canSubmit={canSubmit}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('chat.create.title')}
      context={
        <SegmentedControl
          aria-label={t('chat.create.title')}
          size="sm"
          value={selectedType}
          onChange={handleSelectType}
          options={(['direct', 'group', 'channel'] as NewConversationType[]).map(key => ({
            value: key,
            label: t(`chat.create.type${key === 'direct' ? 'Direct' : key === 'group' ? 'Group' : 'Channel'}`),
            icon: <TypeIcon type={key} />,
          }))}
        />
      }
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError ? (
          <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />
        ) : null}

        {selectedType !== 'direct' && (
          <>
            <TitleInput
              value={name}
              onChange={value => { setName(value); setErrors(prev => ({ ...prev, name: '' })); }}
              placeholder={
                selectedType === 'channel'
                  ? t('chat.create.channelNamePlaceholder')
                  : t('chat.create.groupNamePlaceholder')
              }
              initialFocus
              onEnter={() => document.getElementById('chat-create-description')?.focus()}
              aria-label={t('chat.create.nameLabel')}
            />
            {errors.name && <p className="text-sm text-destructive">{t(errors.name)}</p>}

            {selectedType === 'channel' && (
              <AutoGrowTextarea
                id="chat-create-description"
                value={description}
                onChange={setDescription}
                placeholder={t('chat.create.descriptionPlaceholder')}
                minRows={1}
              />
            )}
          </>
        )}

        <div className="space-y-2 pt-1">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {selectedType === 'direct' ? t('chat.create.selectPerson') : t('chat.create.addMembers')}
          </label>

          {selectedMembers.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedMembers.map(member => (
                <span
                  key={member.entity?.id}
                  className="inline-flex items-center gap-1 rounded-full bg-muted py-1 pl-1 pr-2 text-sm text-foreground"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                    {getInitials(getUserDisplayName(member))}
                  </span>
                  <span className="max-w-[140px] truncate">{getUserDisplayName(member)}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(member.entity?.id ?? '')}
                    className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label={t('common.remove')}
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          <UserPicker
            value=""
            placeholder={t('chat.create.searchPeoplePlaceholder')}
            onChange={handlePick}
          />

          {errors.members && <p className="text-sm text-destructive">{t(errors.members)}</p>}
        </div>
      </div>
    </FormDialog>
  );
});

export default ChatCreatePage;
